import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import {
	ViewState,
	EditingState,
	IntegratedEditing,
    GroupingState,
    IntegratedGrouping,
    Resources
} from '@devexpress/dx-react-scheduler';
import {
	Scheduler,
	DayView,
	WeekView,
	MonthView,
	Appointments,
	AppointmentForm,
	AppointmentTooltip,
	ConfirmationDialog,
	Toolbar,
    AllDayPanel,
    DateNavigator,
    TodayButton,
    GroupingPanel
} from '@devexpress/dx-react-scheduler-material-ui';
import { API_ROUTES } from '../../constants/api_routes';
import { Paper, Grid, makeStyles, Button } from '@material-ui/core';
import ViewSwitcher from './external_view_switcher';
import AppointmentComponent from './appointment_component';
import AppointmentContent from './appointment_content_component';
import CommandLayout from './command_layout';
import DateEditorComponent from './date_editor_component';
import BasicLayout from './basic_layout';
import TextEditor from './text_editor';
import Checkbox from '../ui_components/checkbox_input';
import TextInput from '../ui_components/text_input';
import Autocomplete from '../ui_components/autocomplete';
import Dialog from '../ui_components/dialog_slide';
import BooleanEditor from './boolean_editor';
import Header from './header';
import Content from './content';
import { deleteAppointment, getAppointments } from '../../store/actions/appointments/appointments_action';
import { getPlaces } from '../../store/actions/place/place_action';
import t from './translation.js';
import { useSnackbar } from 'notistack';
import { useForm, FormProvider } from "react-hook-form";
import { getReminderForLoggedUser, setReminderForLoggedUser } from '../../services/local_storage_service';
import LegendComponent from './legend_component';

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(2)
    },
    noFlex: {
        display: 'flex',
        flex: 'none'
    },
    justifyCenter: {
        display: 'flex',
        justifyContent: 'center'
    },
    fullSizeOverlay: {
        '& .MuiPaper-root.MuiDrawer-paper.MuiDrawer-paperAnchorLeft': {
            width: "calc(100% - 50px)"
        }
    }
}));

const SchedulerComponent = ({ places, appointments, user }) => {
    const { container, noFlex, justifyCenter, fullSizeOverlay } = useStyles();
	const [currentDate, setCurrentDate] = useState(new Date());
	const [currentViewName, setCurrentViewName] = useState('Day');
	const [grouping, setGrouping] = useState(true);
    const [customer, setCustomer] = useState(null);
    const [worker, setWorker] = useState(null);
    const [place, setPlace] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [appointmentFormVisible, setAppointmentFormVisible] = useState(false);
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const methods = useForm();

    const { isDoctor } = user;

    useEffect(() => {
        if (!places.length) {
            dispatch(getPlaces({ page: 0, rowsPerPage: 20 }));
        }
        if (!appointments.length) {
            getAppointments(dispatch, { date: currentDate, view: currentViewName });
        }
        if (isDoctor) {
            setGrouping(false);
        }
        if (getReminderForLoggedUser(user.uuid)) {
            methods.setValue('reminder', getReminderForLoggedUser(user.uuid));
        }
    }, [user]);

	const commitChanges = async ({ deleted }) => {
		if (deleted !== undefined) {
            deleteAppointment(dispatch, deleted)
                .then(() => {
                    enqueueSnackbar('Uspešno obrisan termin', { variant: 'success' });
                });
		}
	};

    const placeInstances = useMemo(() => {
        const placeInstances = places?.reduce((accumulator, currentValue) => [...accumulator, { id: currentValue?.uuid, text: `Mesto - ${currentValue?.name}`, color: 'transparent' }], [])
        placeInstances.push({ id: -1, text: 'Termini bez unetih mesta', color: 'transparent' });
        return placeInstances;
    }, [places]);

    // Used after groupped appointment POST/PUT request in order to place it in correct group
    const getAppointmentsAfterRequest = () => {
        grouping && getAppointments(dispatch, { customer, worker, place, date: currentDate, view: currentViewName });
    }

	const currentViewNameChange = (e) => {
        const currentViewName = e.target.value;
		setCurrentViewName(currentViewName);
        getAppointments(dispatch, { customer, worker, place, date: currentDate, view: currentViewName });
	};

    const handleAppointmentFormVisibiltyChange = (visible) => {
        if (visible) {
            setTimeout(() => {
                window.scrollTo({
                    top: 0,
                    behavior: "smooth"
                });
            }, 100);
        }
        setAppointmentFormVisible(visible);
    }

    const handleCheckGrouping = (e) => {
        const grouping = e.target.checked;
        setGrouping(grouping);
    }

    const currentDateChange = (date) => {
        setCurrentDate(date);
        getAppointments(dispatch, { customer, worker, place, date, view: currentViewName });
    }

    const handleCustomerObject = (object) => {
        setCustomer(object);
        getAppointments(dispatch, { customer: object, worker, place, date: currentDate, view: currentViewName });
    }
    const handleWorkerObject = (object) => {
        setWorker(object);
        getAppointments(dispatch, { customer, worker: object, place, date: currentDate, view: currentViewName });
    }
    const handlePlaceObject = (object) => {
        setPlace(object);
        getAppointments(dispatch, { customer, worker, place: object, date: currentDate, view: currentViewName });
    }

    const ReminderComponent = () => (
        <Grid item>
            <TextInput
                name="reminder"
                control={methods.control}
                label="Podsetnik"
                variant="outlined"
                multiline
                fullWidth
                rows={5}
            />
        </Grid>
    );

    const handleOpenDialog = () => {
        setOpenDialog(true);
    }

    const handleCloseDialog = () => {
        setOpenDialog(false);
    }

    const handleSaveDialog = () => {
        const reminder = methods.getValues('reminder');
        setReminderForLoggedUser(user.uuid, reminder);
        setOpenDialog(false);
    }

    const ToolbarRootComponent = useCallback(
        ({ children }) => (
            <Toolbar.Root style={{ height: '100%' }}>
                <Grid container justify="center" className={container}>
                    <Grid item xs={12} className={noFlex}>
                        {children}
                    </Grid>
                </Grid>
            </Toolbar.Root>
    ), []);

    const FilterToolbarComponent = () => (
        <Grid container justify="center" className={container} style={{ display: !appointmentFormVisible ? 'inherit' : 'none'}}>
            <Grid container justify="center">
                <Grid item xs={12} md={4}>
                    <LegendComponent />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Grid item xs={12}>
                        <ViewSwitcher
                            currentViewName={currentViewName}
                            onChange={currentViewNameChange}
                        />
                    </Grid>
                    <Grid item xs={12} className={justifyCenter}>
                        {isDoctor === false &&
                            <Checkbox
                            name="groupByPlace"
                            control={methods.control}
                                value={grouping}
                                onChange={handleCheckGrouping}
                                label="Grupišite po mestu"
                                />
                            }
                        <Button
                            color="primary"
                            onClick={handleOpenDialog}
                            variant="contained"
                            size="small"
                            >
                            Podsetnik
                        </Button>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={4}>
                </Grid>
            </Grid>
                <Grid container justify="center" spacing={2}>
                    <Grid item xs={9} md={4}>
                        <Autocomplete
                            label="Klijent"
                            placeholder="Klijent"
                            api={API_ROUTES.CUSTOMERS}
                            handleObject={handleCustomerObject}
                            preloadValue={customer}
                        />
                    </Grid>
                    <Grid item xs={9} md={4}>
                        {isDoctor === false &&
                            <Autocomplete
                                label="Radnik"
                                placeholder="Radnik"
                                api={API_ROUTES.WORKERS}
                                handleObject={handleWorkerObject}
                                preloadValue={worker}
                            />
                        }
                    </Grid>
                    <Grid item xs={9} md={4}>
                        <Autocomplete
                            label="Mesto"
                            placeholder="Mesto"
                            api={API_ROUTES.PLACES}
                            handleObject={handlePlaceObject}
                            preloadValue={place}
                        />
                    </Grid>
                </Grid>
            </Grid>
    );

    const BasicLayoutComponent = useCallback(
        ({ appointmentData }) => (
            <BasicLayout appointmentData={appointmentData} currentViewName={currentViewName} isDoctor={isDoctor} />
    ), [currentViewName, isDoctor]);

    const ContentComponent = useCallback(
        (props) => (
            <Content defaultProps={props} isDoctor={isDoctor} />
    ), []);

	return (
		<Paper className={fullSizeOverlay}>
            { FilterToolbarComponent() }
            <FormProvider {...methods}>
                <Scheduler firstDayOfWeek={1} data={appointments} locale="sr-Latn">
                    <ViewState
                        currentDate={currentDate}
                        onCurrentDateChange={currentDateChange}
                        currentViewName={currentViewName}
                    />
                    <EditingState
                        onCommitChanges={commitChanges}
                    />
                    {appointments?.length > 0 && grouping &&
                        <GroupingState
                            grouping={[{ resourceName: 'placeUuid' }]}
                        />
                    }
                    <IntegratedEditing />
                    <DayView
                        startDayHour={9}
                        endDayHour={24}
                    />
                    <WeekView
                        startDayHour={9}
                        endDayHour={24}
                    />
                    <MonthView />
                    <ConfirmationDialog messages={t.dialog_confirmation}/>
                    <Toolbar rootComponent={ToolbarRootComponent} />
                    <DateNavigator />
                    <TodayButton messages={t.today_button} />
                    <Appointments
                        appointmentComponent={AppointmentComponent}
                        appointmentContentComponent={AppointmentContent}
                    />
                    <AppointmentTooltip
                        headerComponent={(props) => <Header defaultProps={props} isDoctor={isDoctor} />}
                        contentComponent={ContentComponent}
                        showOpenButton
                        showDeleteButton
                    />
                        <form>
                            <AppointmentForm
                                readOnly={isDoctor === true}
                                messages={t.appointment_form}
                                commandLayoutComponent={(props) => <CommandLayout defaultProps={props} getAppointments={getAppointmentsAfterRequest}/>}
                                basicLayoutComponent={BasicLayoutComponent}
                                dateEditorComponent={DateEditorComponent}
                                booleanEditorComponent={BooleanEditor}
                                textEditorComponent={TextEditor}
                                onVisibilityChange={handleAppointmentFormVisibiltyChange}
                                // overlayComponent={(props) => <AppointmentForm.Overlay {...props} fullSize />} // Not working correctly, but this is based on https://codesandbox.io/p/sandbox/gallant-dhawan-xkdvr5?file=%2Fdemo.js%3A206%2C7-211%2C8
                            />
                        </form>
                    <AllDayPanel messages={t.all_day_panel} />
                    <Resources
                        data={[{
                            fieldName: 'placeUuid',
                            title: 'Mesto',
                            instances: appointments?.length > 0 && grouping ? placeInstances : []
                        }]}
                    />
                    {appointments?.length > 0 && grouping &&
                        <IntegratedGrouping />
                    }
                    {appointments?.length > 0 && grouping &&
                        <GroupingPanel />
                    }
                </Scheduler>
            </FormProvider>
            <Dialog
                title="Podsetnik"
                content={ReminderComponent}
                open={openDialog}
                handleSave={handleSaveDialog}
                handleClose={handleCloseDialog}
            />
		</Paper>
	);
};

SchedulerComponent.propTypes = {
    places: PropTypes.array,
    appointments: PropTypes.array,
    user: PropTypes.object
}

const mapStateToProps = (state) => ({
    places: state.places.collection,
    appointments: state.appointments,
    user: state.user
})

SchedulerComponent.defaultValues = {
    places: [],
    appointments: []
}

export default connect(mapStateToProps)(SchedulerComponent);
